import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, fetchData, deleteData, postData } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';
import { MOMENT_ICONS, getSentimentIcon } from '../utils/iconMappings';
import { 
  Moment, 
  LanguageItem,
  SpeakerItem,
  SentimentItem,
  OccurrenceItem,
  MomentCategoryItem,
  MomentCategoryResponse
} from '../types/api';
import { useLayout } from '../contexts/LayoutContext';
import SearchList, { SearchListConfig, SearchListColumn } from '../components/SearchList';
import { momentFilters } from '../config/filterConfigs';
import { usePagination } from '../hooks/usePagination';
import { FilterAction } from '../components/FilterBar';
import { is_editable } from '../utils/formUtils';
import { 
  FormState, 
  FormErrors, 
  submitMomentData, 
  handleFormInputChange,
  momentToFormState
} from '../utils/momentFormUtils';
import '../styles/screens/MomentsList.css';

interface MomentsListResponse {
  filters: {
        [key: string]: any;
  };
  moments: Moment[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
  };
}

interface EditingMoment extends FormState {
  id: number;
}

const MomentListTable: React.FC = () => {
  const navigate = useNavigate();
  const { updateHeaderProps } = useLayout();
  const [moments, setMoments] = useState<Moment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [editingMoment, setEditingMoment] = useState<EditingMoment | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [editErrors, setEditErrors] = useState<FormErrors>({});

  // Ref for auto-resizing textarea
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Constants for displaying names instead of IDs
  const [languages, setLanguages] = useState<LanguageItem[]>([]);
  const [sentiments, setSentiments] = useState<SentimentItem[]>([]);
  const [occurrences, setOccurrences] = useState<OccurrenceItem[]>([]);
  const [speakers, setSpeakers] = useState<SpeakerItem[]>([]);
  const [categories, setCategories] = useState<MomentCategoryItem[]>([]);

  const [constantsLoaded, setConstantsLoaded] = useState(false);

  // Initialize pagination hook
  const {
    paginationState,
    updatePagination,
    handlePageChange: paginationHandlePageChange,
    handleSort: paginationHandleSort
  } = usePagination(
    { disableAutoUrlUpdate: true },
    { field: 'id', direction: 'asc' }
  );

  // Helper function to get name by ID
  const getNameById = (items: any[], id: number, defaultName = 'Unknown') => {
    const item = items.find(item => item.id === id);
    return item ? item.name : defaultName;
  };

  // Icon mapping functions
  const renderSentimentIcon = (sentimentId: number) => {
    const sentiment = sentiments.find(s => s.id === sentimentId);
    const iconName = getSentimentIcon(sentiment?.name);
    return <span className="material-symbols-outlined">{iconName}</span>;
  };

  const getSpeakerIcon = (speakerId: number) => {
    const speaker = speakers.find(s => s.id === speakerId);
    if (!speaker) return '•';
    
    const name = speaker.name.toLowerCase();
    if (name.includes('agent')) return 'A';
    if (name.includes('customer')) return 'C';
    return '•';
  };

  const getLanguageIcon = (languageId: number) => {
    const language = languages.find(l => l.id === languageId);
    if (!language) return '•';
    
    const name = language.name.toLowerCase();
    if (name.includes('english')) return 'EN';
    if (name.includes('spanish')) return 'ES';
    if (name.includes('french')) return 'FR';
    if (name.includes('german')) return 'DE';
    return '•';
  };

  // Inline editing handlers
  const handleStartEdit = (moment: Moment) => {
    setEditingMoment({
      id: moment.id,
      ...momentToFormState(moment)
    });
  };

  const handleCancelEdit = () => {
    setEditingMoment(null);
    setEditErrors({}); // Clear any existing error messages
  };

  const handleSaveEdit = async () => {
    if (!editingMoment) return;

    try {
      setLoading(true);
      setEditErrors({});
      
      const result = await submitMomentData(editingMoment, 'save', editingMoment.id);
      
      if (result.success) {
        // Update local state
        setMoments(prevMoments => 
          prevMoments.map(moment => 
            moment.id === editingMoment.id 
              ? { 
                  ...moment, 
                  name: editingMoment.name,
                  prompt: editingMoment.prompt,
                  occurrence_id: editingMoment.occurrence_id,
                  language_id: editingMoment.language_id,
                  sentiment_id: editingMoment.sentiment_id,
                  speaker_id: editingMoment.speaker_id,
                  mmt_category_id: editingMoment.mmt_category_id,
                  mmt_category: editingMoment.mmt_category,

                  examples: editingMoment.examples,
                  channels: editingMoment.channels.map(channelId => ({ channel_id: channelId }))
                }
              : moment
          )
        );
        
        setEditingMoment(null);
        console.log('Moment updated successfully');
      } else {
        setEditErrors(result.errors || {});
        console.error('Validation errors:', result.errors);
      }
    } catch (error) {
      console.error('Error updating moment:', error);
      setEditErrors({ general: ['Failed to update moment. Please try again.'] });
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (field: keyof EditingMoment, value: string | number) => {
    if (!editingMoment) return;
    
    if (field === 'mmt_category_id') {
      const numericValue = Number(value);
      const selectedCategory = categories.find(category => category.id === numericValue);
      setEditingMoment(prev => prev ? {
        ...prev,
        mmt_category_id: numericValue,
        mmt_category: selectedCategory ? selectedCategory.name : prev.mmt_category
      } : null);
      if (editErrors['mmt_category_id']) {
        const newErrors = { ...editErrors };
        delete newErrors['mmt_category_id'];
        setEditErrors(newErrors);
      }
      return;
    }

    // Create a synthetic event for the shared handler
    const syntheticEvent = {
      target: { name: field, value: value.toString() }
    } as React.ChangeEvent<HTMLInputElement>;
    
    // Create a wrapper function to handle the type conversion
    const setFormStateWrapper = (newState: FormState) => {
      setEditingMoment(prev => prev ? { ...prev, ...newState } : null);
    };
    
    handleFormInputChange(
      syntheticEvent,
      editingMoment,
      setFormStateWrapper,
      editErrors,
      setEditErrors
    );
  };

  // Auto-resize textarea to fit content and position error
  useEffect(() => {
    if (textareaRef.current && editingMoment) {
      // Reset height to calculate proper size
      textareaRef.current.style.height = "0px";
      const newHeight = textareaRef.current.scrollHeight + "px";
      textareaRef.current.style.height = newHeight;
      
      console.log('Textarea auto-resize:', {
        prompt: editingMoment.prompt,
        scrollHeight: textareaRef.current.scrollHeight,
        newHeight: newHeight,
        currentHeight: textareaRef.current.style.height
      });
    }
  }, [editingMoment, editingMoment?.prompt, editErrors]);



  // Toggle row expansion
  const toggleRowExpansion = (momentId: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(momentId)) {
        newSet.delete(momentId);
      } else {
        newSet.add(momentId);
      }
      return newSet;
    });
  };

  // Define SearchList columns configuration
  const momentColumns: SearchListColumn[] = [
    {
      key: 'id',
      header: 'ID',
      sortable: true,
      width: '0.8fr',
      render: (moment: Moment) => (
        <div className="moment-id-with-status">
          <span className={`status-dot ${moment.is_active === 'True' ? 'active' : 'inactive'}`}></span>
          <span className="moment-id">{moment.id}</span>
        </div>
      ),
      tooltip: (moment: Moment) => `Moment ID: ${moment.id} - ${moment.is_active === 'True' ? 'Active' : 'Inactive'}`
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      width: '1.5fr',
      render: (moment: Moment) => {
        if (editingMoment?.id === moment.id) {
          return (
            <div className="inline-edit-container">
              <input
                type="text"
                value={editingMoment.name}
                onChange={(e) => handleEditChange('name', e.target.value)}
                className={`inline-edit-input ${editErrors.name ? 'error-input' : ''}`}
                autoFocus
                onClick={(e) => e.stopPropagation()}
                title={editingMoment.name} // Add tooltip for full name
              />
              {editErrors.name && (
                <div className="inline-edit-error">
                  {editErrors.name[0]}
                </div>
              )}
            </div>
          );
        }
        return (
          <div className="moment-name" title={moment.name}>
            {moment.name}
          </div>
        );
      },
      tooltip: (moment: Moment) => moment.name
    },

    {
      key: 'prompt',
      header: 'Prompt',
      sortable: false,
      width: '3fr',
      render: (moment: Moment) => {
        if (editingMoment?.id === moment.id) {
          return (
            <div className="inline-edit-container">
              <textarea
                ref={textareaRef}
                value={editingMoment.prompt}
                onChange={(e) => handleEditChange('prompt', e.target.value)}
                className={`inline-edit-textarea ${editErrors.prompt ? 'error-input' : ''}`}
                onClick={(e) => e.stopPropagation()}
                title={editingMoment.prompt} // Add tooltip for full prompt
              />
              {editErrors.prompt && (
                <div className="inline-edit-error">
                  {editErrors.prompt[0]}
                </div>
              )}
            </div>
          );
        }
        // Show full prompt with proper line breaks
        return (
          <div className="moment-prompt full-height" title={moment.prompt}>
            {moment.prompt}
          </div>
        );
      },
      tooltip: (moment: Moment) => moment.prompt
    },
    {
      key: 'mmt_category',
      header: 'Category',
      sortable: true,
      width: '1fr',
      render: (moment: Moment) => {
        if (editingMoment?.id === moment.id) {
          return (
            <select
              value={editingMoment.mmt_category_id}
              onChange={(e) => handleEditChange('mmt_category_id', Number(e.target.value))}
              className="inline-edit-select"
              onClick={(e) => e.stopPropagation()}
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          );
        }
        return (
          <div className="moment-category" title={moment.mmt_category || 'Uncategorized'}>
            <span className="category-name">{moment.mmt_category || 'Uncategorized'}</span>
          </div>
        );
      },
      tooltip: (moment: Moment) => `Category: ${moment.mmt_category || 'Uncategorized'}`
    },
    {
      key: 'language_id',
      header: 'Language',
      sortable: true,
      width: '1fr',
      render: (moment: Moment) => {
        if (editingMoment?.id === moment.id) {
          return (
            <select
              value={editingMoment.language_id}
              onChange={(e) => handleEditChange('language_id', Number(e.target.value))}
              className="inline-edit-select"
              onClick={(e) => e.stopPropagation()}
            >
              {languages.map(language => (
                <option key={language.id} value={language.id}>
                  {language.name}
                </option>
              ))}
            </select>
          );
        }
        return (
          <div className="moment-language">
            <span className="language-icon">{getLanguageIcon(moment.language_id)}</span>
            <span className="language-name">{getNameById(languages, moment.language_id)}</span>
          </div>
        );
      },
      tooltip: (moment: Moment) => `Language: ${getNameById(languages, moment.language_id)}`
    },
    {
      key: 'sentiment_id',
      header: 'Sentiment',
      sortable: true,
      width: '1fr',
      render: (moment: Moment) => {
        if (editingMoment?.id === moment.id) {
          return (
            <select
              value={editingMoment.sentiment_id}
              onChange={(e) => handleEditChange('sentiment_id', Number(e.target.value))}
              className="inline-edit-select"
              onClick={(e) => e.stopPropagation()}
            >
              {sentiments.map(sentiment => (
                <option key={sentiment.id} value={sentiment.id}>
                  {sentiment.name}
                </option>
              ))}
            </select>
          );
        }
        return (
          <div className="moment-sentiment">
            {/* <span className="sentiment-icon">{renderSentimentIcon(moment.sentiment_id)}</span> */}
            <span className="sentiment-icon material-symbols-outlined">{getSentimentIcon(getNameById(sentiments, moment.sentiment_id))}</span>
            <span className="sentiment-name">{getNameById(sentiments, moment.sentiment_id)}</span>
          </div>
        );
      },
      tooltip: (moment: Moment) => `Sentiment: ${getNameById(sentiments, moment.sentiment_id)}`
    },
    {
      key: 'speaker_id',
      header: 'Speaker',
      sortable: true,
      width: '1fr',
      render: (moment: Moment) => {
        if (editingMoment?.id === moment.id) {
          return (
            <select
              value={editingMoment.speaker_id}
              onChange={(e) => handleEditChange('speaker_id', Number(e.target.value))}
              className="inline-edit-select"
              onClick={(e) => e.stopPropagation()}
            >
              {speakers.map(speaker => (
                <option key={speaker.id} value={speaker.id}>
                  {speaker.name}
                </option>
              ))}
            </select>
          );
        }
        return (
          <div className="moment-speaker">
            <span className="speaker-icon">{getSpeakerIcon(moment.speaker_id)}</span>
            <span className="speaker-name">{getNameById(speakers, moment.speaker_id)}</span>
          </div>
        );
      },
      tooltip: (moment: Moment) => `Speaker: ${getNameById(speakers, moment.speaker_id)}`
    },


  ];

  // SearchList configuration
  const searchListConfig: SearchListConfig = {
    columns: momentColumns,
    filterConfig: {
      ...momentFilters,
      filters: momentFilters.filters.map(filter => {
        // Populate dynamic options based on loaded constants
        if (filter.dynamicOptions) {
          let options: Array<{ value: string; label: string }> = [];
          
          if (filter.key === 'language_id') {
            options = languages.map(lang => ({ value: lang.name, label: lang.name }));
          } else if (filter.key === 'sentiment_id') {
            options = sentiments.map(sent => ({ value: sent.name, label: sent.name }));
          } else if (filter.key === 'occurrence_id') {
            options = occurrences.map(occ => ({ value: occ.name, label: occ.name }));
          } else if (filter.key === 'speaker_id') {
            options = speakers.map(speaker => ({ value: speaker.name, label: speaker.name }));
          }
          
          return {
            ...filter,
            options
          };
        }
        return filter;
      })
    },
    searchPlaceholder: "Search moments by name or prompt...",
    showSearch: true,
    showFilterButton: true,
    showFilterBar: true,
    showCreateButton: true,
    showBuildUpload: true,
    itemsPerPage: 10,
    defaultSort: { field: 'id', direction: 'asc' },
    exportConfig: {
      enabled: true,
      filenameBase: 'moments',
      columns: [
        { header: 'id', value: (item: any) => String(item["id"]) },
        { header: 'name', value: (item: any) => String(item["name"]) },
        { header: 'search_text', value: (item: any) => String(item["prompt"]) },
        { header: 'language', value: (item: any) => String(item["language_name"]) },
        { header: 'sentiment', value: (item: any) => String(item["sentiment_name"]) },
        { header: 'speaker', value: (item: any) => String(item["speaker_name"]) },
        { header: 'category', value: (item: any) => String(item["mmt_category"]) },
        { header: 'is_active', value: (item: any) => String(item["is_active"]) },
        { header: 'location', value: (item: any) => String(item["location"]) },
        { header: 'examples', value: (item: any) => String(item["examples"]) },
        { header: 'channel1', value: (item: any) => String(item["channels"]?.[0]?.["channel_name"] || '') },
        { header: 'channel2', value: (item: any) => String(item["channels"]?.[1]?.["channel_name"] || '') },
        { header: 'channel3', value: (item: any) => String(item["channels"]?.[2]?.["channel_name"] || '') },
        { header: 'search_text_location', value: (item: any) => "TEXT" },
        { header: 'search_type', value: (item: any) => "PROMPT" },

      ],
      // PDF-specific configuration
      pdfColumnWidths: {
        'id': '0.7fr',        // Reduced from 0.8fr to save space
        'prompt': '3.3fr',    // Reduced from 3fr to give more space to later columns
        'sentiment_id': '1.2fr', // Increased from 1fr to fit 2 more characters
        'mmt_category': '1fr',
        'language_id': '1fr',   // Increased from default to fit text
        'speaker_id': '1fr'     // Increased from default to fit text
      }
    }
  };

  // Footer function to show notes and actions
  const renderFooter = (moment: Moment) => {
    // Parse examples if available
    let examples: string[] = [];
    if (moment.examples) {
      examples = moment.examples.split(/[;\n]/).filter(ex => ex.trim());
    }

    // Show as many examples as possible that fit in the available space
    // Show all examples - CSS will handle overflow naturally
    const examplesToShow = examples;
    const hasMoreExamples = false; // CSS handles overflow, no need for manual ellipsis

    return (
      <>
        <div className="footer-notes">
          {examples.length > 0 ? (
            <div className="notes-list">
              <span className="notes-counter">{examples.length} Example{examples.length > 1 ? 's' : ''}:</span>
              {examplesToShow.map((example, index) => (
                <span key={index} className="note-item">
                  {index + 1}. {example}
                </span>
              ))}

            </div>
          ) : (
            <span>No examples</span>
          )}
        </div>
        <div className="footer-actions">
          {editingMoment?.id === moment.id ? (
            <>
              <button 
                className="save-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveEdit();
                }}
                title="Save changes"
              >
                <span className="material-icons">save</span>
              </button>
              <button 
                className="cancel-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelEdit();
                }}
                title="Cancel editing"
              >
                <span className="material-icons">close</span>
              </button>
            </>
          ) : (
            <>
              <button 
                className="edit-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartEdit(moment);
                }}
                title="Edit moment"
                disabled={!is_editable(moment.state_id)}
              >
                <span className="material-icons" style={{ fontSize: '16px' }}>edit</span>
              </button>
              <button 
                className="view-button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/moment/${moment.id}`);
                }}
                title="View moment"
              >
                <span className="material-icons" style={{ fontSize: '16px' }}>visibility</span>
              </button>
              <button 
                className="copy-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyMoment(moment.id);
                }}
                title="Copy moment"
              >
                <span className="material-icons" style={{ fontSize: '16px' }}>content_copy</span>
              </button>
              <button 
                className="delete-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteMoment(moment.id);
                }}
                title="Delete moment"
                disabled={moment.is_active !== 'True'}
              >
                <span className="material-icons" style={{ fontSize: '16px' }}>delete</span>
              </button>
            </>
          )}
        </div>
      </>
    );
  };

  // Notes function to show examples below each row
  const renderNotes = (moment: Moment) => {
    if (!expandedRows.has(moment.id) || !moment.examples) {
      return null;
    }

    const examples = moment.examples.split(/[;\n]/).filter(ex => ex.trim());
    
    if (examples.length === 0) {
      return (
        <div className="moment-examples">
          <div className="examples-header">
            <span className="examples-title">Examples</span>
          </div>
          <div className="no-examples">No examples available</div>
        </div>
      );
    }

    return (
      <div className="moment-examples">
        <div className="examples-header">
          <span className="examples-title">Examples ({examples.length})</span>
        </div>
        <div className="examples-list">
          {examples.map((example, index) => (
            <div key={index} className="example-item">
              <span className="example-number">{index + 1}.</span>
              <span className="example-text">{example.trim()}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  useEffect(() => {
    updateHeaderProps({
      pageTitle: 'Moments',
      showInteractionExId: false,
    });
  }, []);

  // Load constants data
  useEffect(() => {
    const loadConstants = async () => {
      try {
        const [
          languagesRes, 
          sentimentsRes, 
          occurrencesRes, 
          speakersRes,
          categoriesRes
        ] = await Promise.all([
          fetchData<{languages: LanguageItem[]}>(api(PATH_SEGMENTS.LANGUAGE)),
          fetchData<{sentiments: SentimentItem[]}>(api(PATH_SEGMENTS.SENTIMENT)),
          fetchData<{occurrences: OccurrenceItem[]}>(api(PATH_SEGMENTS.OCCURRENCE)),
          fetchData<{speakers: SpeakerItem[]}>(api(PATH_SEGMENTS.SPEAKER)),
          fetchData<MomentCategoryResponse>(`${api(PATH_SEGMENTS.MOMENT, PATH_SEGMENTS.CATEGORY)}/?all_results=true`)
        ]);
        
        setLanguages(languagesRes.languages || []);
        setSentiments(sentimentsRes.sentiments || []);
        setOccurrences(occurrencesRes.occurrences || []);
        setSpeakers(speakersRes.speakers || []);
        setCategories(categoriesRes.mmt_categories || []);
        setConstantsLoaded(true);
      } catch (error) {
        console.error("Error loading constants:", error);
        setConstantsLoaded(true);
      }
    };
    
    loadConstants();
  }, []);

  // Shared function to fetch moments from API
  const fetchMomentsFromAPI = async (options: {
    filters: Record<string, any>;
    sort: { field: string; direction: 'asc' | 'desc' };
    searchQuery?: string;
    pagination?: { page: number; per_page: number };
    allResults?: boolean;
  }): Promise<MomentsListResponse> => {
    const params = new URLSearchParams();
    
    // Set pagination or all_results flag
    if (options.allResults) {
      params.set('all_results', 'True');
    } else if (options.pagination) {
      params.set('page', options.pagination.page.toString());
      params.set('per_page', options.pagination.per_page.toString());
    }
    
    // Set sorting
    params.set('order_by', options.sort.field);
    params.set('order_direction', options.sort.direction);
    
    // Set search query if provided
    if (options.searchQuery) {
      params.set('search', options.searchQuery);
    }
    
    // Add filters
    const apiFilters: Record<string, any> = {};
    Object.entries(options.filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        apiFilters[key] = value;
      }
    });

    Object.entries(apiFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, v.toString()));
      } else {
        params.set(key, value.toString());
      }
    });

    return await fetchData<MomentsListResponse>(
      `${api(PATH_SEGMENTS.MOMENT)}?${params.toString()}`
    );
  };

  // Load moments data
  const loadMoments = async () => {
    if (!constantsLoaded) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchMomentsFromAPI({
        filters,
        sort: paginationState.sortConfig,
        searchQuery,
        pagination: {
          page: paginationState.currentPage,
          per_page: paginationState.itemsPerPage
        }
      });
      
      setMoments(response.moments || []);
      
      if (response.pagination) {
        updatePagination({
          totalPages: response.pagination.total_pages,
          itemsPerPage: response.pagination.items_per_page,
          sortConfig: paginationState.sortConfig
        });
      } else {
        updatePagination({
          totalPages: 1,
          itemsPerPage: paginationState.itemsPerPage,
          sortConfig: paginationState.sortConfig
        });
      }
    } catch (err) {
      console.error("Error loading moments:", err);
      setError(err instanceof Error ? err.message : 'Failed to load moments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (constantsLoaded) {
      loadMoments();
    }
  }, [constantsLoaded, paginationState.currentPage, paginationState.sortConfig, filters]);

  // Fetch all results for export (no pagination, all_results=True)
  const fetchAllResults = async (args: { filters: Record<string, any>; sort: { field: string; direction: 'asc' | 'desc' } }) => {
    if (!constantsLoaded) return [];
    
    try {
      const response = await fetchMomentsFromAPI({
        filters: args.filters,
        sort: args.sort,
        searchQuery,
        allResults: true
      });
      
      return response.moments || [];
    } catch (err) {
      console.error("Error fetching all results:", err);
      return [];
    }
  };

  // Handle search change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  // Handle search submit
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updatePagination({ currentPage: 1 });
    if (searchQuery) {
      setFilters(prevFilters => ({ ...prevFilters, search: searchQuery }));
      setSearchQuery('');
    }
  };

  // Handle filter change
  const handleFilterChange = (newFilters: Record<string, any>) => {
    setFilters(newFilters);
    updatePagination({ currentPage: 1 });
    // loadMoments();
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    paginationHandlePageChange(page);
  };

  // Handle sort
  const handleSort = (field: string) => {
    paginationHandleSort(field);
  };

  // Handle moment click
  const handleMomentClick = (moment: Moment) => {
    navigate(`/moment/${moment.id}`);
  };

  // Handle create new moment
  const handleCreateNew = () => {
    navigate('/moment');
  };

  // Handle delete moment
  const handleDeleteMoment = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this moment?')) {
      try {
        setLoading(true);
        await deleteData(api(PATH_SEGMENTS.MOMENT, id.toString()));
        setMoments(prevMoments => prevMoments.filter(moment => moment.id !== id));
        console.log('Moment deleted successfully');
      } catch (error) {
        console.error('Error deleting moment:', error);
        alert('Failed to delete moment. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle copy moment
  const handleCopyMoment = (id: number) => {
    navigate('/moment/', { 
      state: { 
        copyFrom: moments.find(m => m.id === id),
        isCopy: true
      } 
    });
  };

  // Handle CSV upload
  const handleUpload = async (file: File, updateExisting: boolean): Promise<{
    message?: string;
    data: {
      inserted_count?: number;
      updated_count?: number;
      skipped_count?: number;
      failure_count?: number;
      errors?: Array<{ row: number; data: any; error: string }>;
    };
  }> => {
    const endpoint = api(PATH_SEGMENTS.MOMENT) + '/bulk_upload/';
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams();
    if (updateExisting) {
      params.set('update_existing', 'True');
    }

    const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;

    const response = await postData<any>(
      url,
      formData,
      'POST',
      'multipart/form-data'
    );

    // Handle response structure: { status_code, content }
    return response.content || response;
  };

  return (
    <div className="moments-list-container">
      <SearchList
        config={searchListConfig}
        data={moments}
        loading={loading}
        error={error}
        filters={filters}
        searchQuery={searchQuery}
        currentPage={paginationState.currentPage}
        totalPages={paginationState.totalPages}
        itemsPerPage={paginationState.itemsPerPage}
        sortConfig={paginationState.sortConfig}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        onFilterChange={handleFilterChange}
        onFilterActionChange={() => {}}
        onPageChange={handlePageChange}
        onSort={handleSort}
        onItemClick={handleMomentClick}
        onCreateClick={handleCreateNew}
        onUpload={handleUpload}
        onUploadSuccess={loadMoments}
        emptyMessage="No moments found"
        loadingMessage="Loading moments..."
        footer={renderFooter}
        getRowClassName={(item: Moment) => item.is_active !== 'True' ? 'inactive' : ''}
        fetchAllResults={fetchAllResults}
      />
    </div>
  );
};

export default MomentListTable; 